import { createSlice } from '@reduxjs/toolkit';
import { db } from '../lib/database';

const initialState = {
  items: [],
  status: 'idle',
  error: null,
  currentPage: 1,
  hasMore: true
};

const submissionsSlice = createSlice({
  name: 'submissions',
  initialState,
  reducers: {
    setLoading: state => {
      state.status = 'loading';
    },
    setSuccess: (state, action) => {
      const newItems = action.payload.data || [];
      const existingIds = new Set(state.items.map(item => item.id));
      const uniqueNewItems = newItems.filter(item => !existingIds.has(item.id));

      state.status = 'succeeded';
      state.items = action.payload.forceRefresh ? newItems : state.currentPage === 1 ? newItems : [...state.items, ...uniqueNewItems];
      state.hasMore = action.payload.hasMore ?? newItems.length > 0;
      if (action.payload.page) {
        state.currentPage = action.payload.page;
      }
    },
    setError: (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    },
    resetSubmissions: () => initialState,
    addSubmission: (state, action) => {
      state.items.unshift(action.payload);
    },
    removeSubmission: (state, action) => {
      state.items = state.items.filter(item => item.id !== action.payload);
    }
  }
});

export const { setLoading, setSuccess, setError, resetSubmissions, addSubmission, removeSubmission } = submissionsSlice.actions;

const enrichSubmissionsWithFormNames = submissions => {
  const formIds = [...new Set(submissions.map(s => s.form_id))];

  return db.forms
    .where('id')
    .anyOf(formIds)
    .toArray()
    .then(forms => {
      const formMap = new Map(forms.map(f => [String(f.id), f]));

      return submissions.map(sub => {
        const form = formMap.get(String(sub.form_id));
        const embeddedForm = sub.form || {};

        return {
          ...sub,
          formName: form ? form.title : embeddedForm.name,
          formStatus: form ? form.status : embeddedForm.status || 'draft'
        };
      });
    });
};

export const fetchSubmissions =
  ({ page, limit, forceRefresh = false, formId = null }) =>
  async (dispatch, getState) => {
    const { items } = getState().submissions;

    if (items.length === 0 || forceRefresh) {
      dispatch(setLoading());
    }

    const loadFromDb = () => {
      return db.submissions
        .toArray()
        .then(localSubmissions => enrichSubmissionsWithFormNames(localSubmissions))
        .then(enrichedData => {
          dispatch(setSuccess({ data: enrichedData, hasMore: false, page: 1, forceRefresh: true }));
          return true;
        });
    };

    await loadFromDb();

    if (navigator.onLine) {
      const { submissionService } = await import('../services/submissionService');
      return submissionService
        .fetchSubmissions(page, limit)
        .then(response => {
          const apiData = response.data || { data: [], hasMore: false };
          const hadData = apiData.data && apiData.data.length > 0;

          if (formId) {
            db.submissionMeta.put({
              form_id: formId,
              lastFetchHadData: hadData,
              lastFetchTime: Date.now()
            });
          } else {
            db.submissionMeta.put({
              form_id: 'all',
              lastFetchHadData: hadData,
              lastFetchTime: Date.now()
            });
          }

          return db.submissions
            .where('syncStatus')
            .equals('pending')
            .toArray()
            .then(unsyncedSubmissions => {
              const apiIds = new Set(apiData.data.map(s => s.id));
              const uniqueUnsynced = unsyncedSubmissions.filter(s => !apiIds.has(s.id));
              const merged = [...uniqueUnsynced, ...apiData.data];
              return enrichSubmissionsWithFormNames(merged);
            })
            .then(enrichedData => {
              dispatch(setSuccess({ ...apiData, data: enrichedData, page, forceRefresh }));
              return enrichedData;
            });
        })
        .catch(error => {
          const isUnauthorized = error?.response?.status === 403 || error?.response?.status === 401;
          const errMsg = error.response?.data?.message || error.message || 'Failed to fetch submissions.';

          if (isUnauthorized) {
            if (formId) {
              db.submissionMeta.put({
                form_id: formId,
                lastFetchHadData: false,
                lastFetchTime: Date.now()
              });
            } else {
              db.submissionMeta.put({
                form_id: 'all',
                lastFetchHadData: false,
                lastFetchTime: Date.now()
              });
            }
            dispatch(setError(errMsg));
          } else {
            const { items: currentItems } = getState().submissions;
            if (currentItems.length === 0) {
              dispatch(setError(errMsg));
            }
          }
          throw error;
        });
    }
  };

export default submissionsSlice.reducer;
